/*
 * RUG-CART — ONE WAY TO PUT A LINE IN THE CART, FOR EVERY SURFACE OF OURS
 * THAT DOES IT OUTSIDE THE PRODUCT FORM.
 *
 * ── WHY THIS FILE EXISTS (2026-09-06) ──────────────────────────────────────
 * Reported as "pressing add to cart in the popup takes too long". MEASURED on
 * the live storefront before anything was changed, and the timing was not the
 * problem:
 *
 *     selectOnPage(), the synchronous half of the handler   0.2 - 1.6 ms
 *     POST /cart/add.js                                     363 - 771 ms
 *
 * 500 ms is Shopify's own cart write and the page's OWN Add to cart button
 * pays exactly the same. Nothing in our code was waiting on anything it did
 * not need.
 *
 * **WHAT WAS ACTUALLY WRONG IS THAT THE CART NEVER SHOWED IT.** Measured: the
 * server cart went 7 -> 8 in 435 ms and the header bubble still read `0` and
 * still carried `visually-hidden`. So the only confirmation a customer got was
 * a button label changing inside the popup they were about to close - which is
 * indistinguishable from nothing having happened, and is why it read as slow.
 *
 * THE CAUSE IS A ONE-WORD SHAPE MISMATCH, NOT A WRONG EVENT NAME. Both
 * surfaces dispatched
 *
 *     new CustomEvent('shopify:cart:lines-update', { detail: { source: … } })
 *
 * and `shopify:cart:lines-update` IS the right name - it is exactly
 * `StandardEvents.cartLinesUpdate`, read out of the live
 * `@shopify/events` module. But every one of the TWELVE listeners in this
 * theme reads `event.promise`:
 *
 *     onCartUpdate = (event) => { event.promise?.then(({cart, detail}) => …) }
 *
 * A plain CustomEvent has no `promise`, so `?.` short-circuits and the handler
 * does nothing AT ALL, silently, in cart-icon, cart-drawer, header-actions,
 * component-cart-items, product-form, sticky-add-to-cart and six more.
 * **An event with the right name and the wrong shape is worse than no event:
 * it looks like the notification is wired up.**
 *
 * ── WHAT IT DOES INSTEAD, WHICH IS WHAT HORIZON ITSELF DOES ────────────────
 * `assets/product-form.js` is the canonical path and this follows it exactly:
 *   1. build a deferred promise with `CartLinesUpdateEvent.createPromise()`;
 *   2. DISPATCH THE EVENT FIRST, BEFORE THE FETCH, carrying that promise, so
 *      every listener starts reacting on the PRESS rather than a round trip
 *      later - that ordering is the whole of "immediately", and it is the one
 *      thing about the latency we can actually change;
 *   3. POST the line;
 *   4. re-read `/cart.js` and RESOLVE with
 *      `CartLinesUpdateEvent.createCartFromAjaxResponse(cart)`, because
 *      `/cart/add.js` returns the added items and NOT `item_count`, and the
 *      count is what the bubble renders;
 *   5. reject on failure, so a listener that is waiting is told rather than
 *      left waiting.
 *
 * ── ONE FILE, TWO CALLERS, DELIBERATELY ───────────────────────────────────
 * `rug-visualiser.js` and `rug-finder.js` BOTH carried the same inert
 * dispatch, written twice. CLAUDE.md records what a second implementation
 * costs three separate times - three retry policies, GENERATION_MODE,
 * PIPELINE_PATHS - and this is the same shape: two copies of "put it in the
 * cart", which is why one of them being wrong meant both were. Do not add a
 * third; call `window.RugCart.add` or `window.RugCart.update`.
 *
 * `update()` JOINED IT ON 2026-09-08 for the free-gift reconciler, and the two
 * share `announce()` rather than each carrying the dance - which is the same
 * rule one level down. `add` puts ONE line in; `update` sets ABSOLUTE
 * quantities for several at once, which is what lets a reconciler add five
 * gifts, hold them at one, or take all five away in a single request.
 *
 * ── SHOPIFY TRANSPILES THEME ASSETS, AND IT TURNS `import()` INTO `require()`
 * (MEASURED 2026-09-06) ───────────────────────────────────────────────────
 * The first version of this file read the page's importmap for the real URL of
 * `@shopify/events` and called `import(spec)`. It failed on every press, and
 * the reason is not in this repository: **Shopify's asset pipeline runs a
 * `.js` asset through a bundler**, and the deployed file came back as
 *
 *     Promise.resolve().then(function(){return __toESM(require(spec))})
 *
 * alongside injected `__toESM` / `__copyProps` helpers. `require` does not
 * exist in a browser, so the promise rejected with a ReferenceError, the
 * fallback fired, and the console said `@shopify/events unavailable` on a page
 * where the module loads perfectly from the devtools console.
 *
 * **A DYNAMIC `import()` IN A THEME ASSET IS NOT A DYNAMIC IMPORT.** It is
 * source the bundler rewrites into a Node idiom. Nothing in the theme, the
 * push or the verify can see it - `theme_push` reads back byte-identical
 * because the SOURCE is what is stored; the transform happens when the asset
 * is SERVED. The only way to find it is to fetch the deployed asset and read
 * it.
 *
 * SO THE MODULE IS LOADED BY AN INJECTED `<script type="module">` INSTEAD.
 * Its body is a runtime STRING, so there is nothing for a bundler to see, and
 * a real module script resolves `@shopify/events` through the page's own
 * importmap rather than through a URL we copied out of it. Warmed at load, so
 * a press never waits on it.
 *
 * IF THE MODULE CANNOT BE RESOLVED, THE LINE IS STILL ADDED. The event is what
 * degrades, never the cart write: a customer whose rug is in the cart with a
 * stale bubble is in a far better position than one whose press did nothing
 * because a notification helper failed. That fallback is why the transpiler
 * cost a stale cart icon rather than a lost sale, and its console warning is
 * how the cause was found.
 */
(function () {
  'use strict';

  /* DEFINED ONCE PER PAGE, WHATEVER LOADS IT. `theme.liquid` loads this on
   * every page for the gift reconciler, and the visualiser block and the rug
   * finder section each load it too — a duplicate `<script src>` is fetched
   * once and EXECUTED twice. Worse, Horizon re-inserts the whole
   * product-information section on every variant change and a re-inserted
   * script element re-executes, so on a product page this ran again for every
   * size the customer tried. Each run appended another `<script type="module">`
   * loader (`INJECTED` is per-execution) and republished `window.__rugCartEvents`.
   * Nothing broke, which is exactly why it would have gone on not breaking. */
  if (window.RugCart) return;

  var EVENTS = null;      /* the settled module, or null while unknown */
  var INJECTED = false;   /* the module script is appended at most once */

  /* THE BODY IS A STRING SO NO BUNDLER CAN SEE THE IMPORT. It publishes the
   * namespace on `window` and says so with an event; both are read below,
   * because a module script gives no promise of its own to await. */
  var LOADER =
    "import * as m from '@shopify/events';" +
    "window.__rugCartEvents = m;" +
    "document.dispatchEvent(new CustomEvent('rugcart:events'));";

  function loadEvents() {
    if (EVENTS) return EVENTS;
    EVENTS = new Promise(function (resolve, reject) {
      /* ALREADY THERE — resolve without touching the DOM. True on a retry, and
       * true if anything else on the page has published it. */
      if (window.__rugCartEvents) return resolve(window.__rugCartEvents);

      var done = false;
      var settle = function () {
        if (done) return;
        done = true;
        if (window.__rugCartEvents) resolve(window.__rugCartEvents);
        else reject(new Error('@shopify/events did not load'));
      };
      document.addEventListener('rugcart:events', settle, { once: true });
      /* A LAST RESORT, NOT A WAIT ANYONE PAYS FOR. An inline module that fails
       * to resolve its specifier fires no `error` event, so silence has to be
       * bounded by something - and because this is warmed at load, the timer
       * has always finished long before a customer presses anything. */
      setTimeout(settle, 4000);

      if (!INJECTED) {
        INJECTED = true;
        var s = document.createElement('script');
        s.type = 'module';
        s.textContent = LOADER;
        s.onerror = settle;
        (document.head || document.documentElement).appendChild(s);
      }
    });
    /* A FAILURE IS NOT REMEMBERED FOREVER. Caching the rejected promise made
     * one bad moment permanent for the life of the page; clearing it lets the
     * next press re-check `window.__rugCartEvents`, which is instant and
     * appends nothing because `INJECTED` is already true. */
    EVENTS['catch'](function () { EVENTS = null; });
    return EVENTS;
  }
  try { loadEvents()['catch'](function () {}); } catch (e) { /* warm-up only */ }

  function postLine(variantId, qty, properties) {
    var item = { id: variantId, quantity: qty };
    /* LINE-ITEM PROPERTIES RIDE ALONG AND REACH THE ORDER. Measured on the live
     * store 2026-09-08: `/cart/add.js` IGNORES a `price` field outright and
     * takes the variant's own, while storing `properties` verbatim and carrying
     * them to the order, the confirmation email and the packing slip. That is
     * the whole reason a custom size records what the customer typed HERE
     * rather than in a system fulfilment would have to be told to open. */
    if (properties && Object.keys(properties).length) item.properties = properties;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ items: [item] })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart');
      return r.json();
    });
  }

  /* THE COUNT COMES FROM `/cart.js`, NOT FROM THE ADD RESPONSE, and the extra
   * round trip is what Horizon's own `#refreshCart()` does for the same
   * reason: `/cart/add.js` answers with the LINES IT ADDED and carries no
   * `item_count`, so a bubble computed from it would drift the moment the cart
   * already held something. It happens AFTER the button has already been told
   * the line landed, so nobody waits on it. */
  function readCart() {
    return fetch('/cart.js', { headers: { accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  /* POST an ABSOLUTE set of quantities. `/cart/update.js` is the only cart
   * endpoint that can add, change and remove in ONE request, and it takes
   * quantities rather than deltas — measured on the live store: five variants
   * NOT in the cart went in with one call, a second rug left every gift at 1
   * rather than 2, and one further call cleared all six lines. That is exactly
   * the shape a reconciler needs, and it is why this is not `/cart/change.js`
   * once per line. */
  function postUpdates(updates, attributes) {
    var body = { updates: updates };
    /* CART ATTRIBUTES RIDE THE SAME CALL. They become the ORDER's
     * note_attributes, which is where a person doing fulfilment reads them,
     * and sending them separately would be a second write that could land
     * without the first. An empty-string value REMOVES an attribute, which is
     * how the note comes off a cart that no longer carries gifts. */
    if (attributes) body.attributes = attributes;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('cart');
      return r.json();
    });
  }

  /**
   * DO THE WRITE AND TELL THE THEME ABOUT IT, IN THE ORDER THE THEME EXPECTS.
   *
   * Extracted so `add` and `update` share ONE copy of the dance rather than
   * two — which is the same rule this file's own header states about the two
   * surfaces that used to dispatch an inert event each. The order is the whole
   * of "immediately": dispatch first so every listener reacts to the PRESS,
   * then write, then resolve from a fresh `/cart.js`.
   *
   * @param {string} action    'add' | 'update' | 'remove' — what a listener animates on
   * @param {string} context   'product' | 'cart' — where the change came from
   * @param {Array}  lines     [{merchandiseId, quantity}]
   * @param {string} src       a name for the surface
   * @param {Function} work    returns a promise for the cart write itself
   */
  function announce(action, context, lines, src, work) {
    return loadEvents().then(function (m) {
      var Ev = m && m.CartLinesUpdateEvent;
      if (!Ev) throw new Error('no CartLinesUpdateEvent');
      var deferred = Ev.createPromise();

      /* DISPATCHED BEFORE THE FETCH, WHICH IS THE POINT. Horizon's own
       * product form does exactly this, and it is what lets the fly-to-cart
       * and the drawer react to the press instead of to the response. */
      document.dispatchEvent(new Ev({
        action: action,
        context: context,
        source: src,
        lines: lines,
        promise: deferred.promise
      }));

      return work().then(function (res) {
        readCart().then(function (cart) {
          deferred.resolve({
            cart: Ev.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              source: src,
              /* THE CART'S OWN COUNT, not the number of lines touched. Only
               * ever a fallback — `cart-icon.js` reads `cart.totalQuantity`
               * first and that always resolves here — but a fallback that is
               * wrong is worse than one that is never used. */
              itemCount: cart.item_count,
              didError: false
            }
          });
        }, function (err) { deferred.reject(err); });
        return res;
      }, function (err) {
        deferred.reject(err);
        throw err;
      });
    }, function () {
      /* NO EVENT MODULE — STILL WRITE THE CART. Reported to the console because
       * a cart that changes without its icon moving is a real defect, just not
       * one worth refusing a sale over. */
      return work().then(function (res) {
        try { console.warn('[rug-cart] wrote the cart without notifying the theme: @shopify/events unavailable'); } catch (e) {}
        return res;
      });
    });
  }

  /**
   * Add one variant, and tell the whole theme about it the way the theme
   * expects to be told.
   *
   * Resolves as soon as the LINE IS IN THE CART - not when the bubble has
   * caught up - so a caller can say "Added" at the earliest honest moment.
   *
   * @param {string|number} variantId
   * @param {number} [qty=1]
   * @param {string} [source] a name for the surface, carried on the event
   * @param {Object} [properties] line-item properties, carried to the ORDER.
   *                 A key starting with `_` is hidden from the customer but
   *                 still reaches the order — that is where a custom size
   *                 records what was typed as opposed to what is woven.
   * @returns {Promise<object>} the /cart/add.js response
   */
  function add(variantId, qty, source, properties) {
    var quantity = qty || 1;
    return announce('add', 'product',
                    [{ merchandiseId: String(variantId), quantity: quantity }],
                    source || 'rugzella',
                    function () { return postLine(variantId, quantity, properties); });
  }

  /**
   * Set absolute quantities for one or more variants, in a single request.
   *
   * A quantity of 0 removes the line. An empty object is a no-op that makes NO
   * request and dispatches NO event — a reconciler that finds nothing to do
   * must be free, or every cart change would cost a pointless round trip and a
   * pointless re-render.
   *
   * @param {Object} updates  { variantId: quantity }
   * @param {string} [source]
   * @param {Object} [attributes] cart attributes to set alongside; a value of
   *                 '' removes one. They become the order's note_attributes.
   * @returns {Promise<object|null>} the /cart/update.js response, or null
   */
  function update(updates, source, attributes) {
    var ids = Object.keys(updates || {});
    if (!ids.length) return Promise.resolve(null);
    var lines = ids.map(function (id) {
      return { merchandiseId: String(id), quantity: updates[id] };
    });
    var gone = lines.every(function (l) { return l.quantity === 0; });
    return announce(gone ? 'remove' : 'update', 'cart', lines,
                    source || 'rugzella',
                    function () { return postUpdates(updates, attributes); });
  }

  window.RugCart = { add: add, update: update };
})();
